/**
 * Hook to read/write a user's saved signature, stored as a Basic resource in Medplum.
 *
 * Follows the same pattern as useTemplateSettings (Basic resource + extension JSON).
 * The Basic resource is scoped to a Practitioner via `subject`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Basic } from '@medplum/fhirtypes';
import {
  USER_SIGNATURE_CONFIG_CODE,
  USER_SIGNATURE_CONFIG_DATA_URL,
  USER_SIGNATURE_CONFIG_SYSTEM,
} from '@unify/fhir-model/constants';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';

export interface UserSignatureConfig {
  savedSignature?: SignatureInfo;
  preferredMode: 'type' | 'draw';
}

const DEFAULT_CONFIG: UserSignatureConfig = { preferredMode: 'type' };

export interface UseUserSignatureResult {
  config: UserSignatureConfig;
  loading: boolean;
  saveConfig: (config: UserSignatureConfig) => Promise<void>;
}

export function useUserSignature(): UseUserSignatureResult {
  const medplum = useMedplum();
  const { practitioner, loading: practLoading } = useCurrentUserPractitioner();
  const [config, setConfig] = useState<UserSignatureConfig>(DEFAULT_CONFIG);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (practLoading || !practitioner?.id) {
      setLoading(practLoading);
      return;
    }
    let cancelled = false;

    async function fetchConfig() {
      setLoading(true);
      try {
        const bundle = await medplum.search('Basic', {
          code: `${USER_SIGNATURE_CONFIG_SYSTEM}|${USER_SIGNATURE_CONFIG_CODE}`,
          subject: `Practitioner/${practitioner!.id}`,
          _count: '1',
        });
        if (cancelled) return;

        const resource = bundle.entry?.[0]?.resource as Basic | undefined;
        if (resource) {
          setResourceId(resource.id ?? null);
          const ext = resource.extension?.find((e) => e.url === USER_SIGNATURE_CONFIG_DATA_URL);
          if (ext?.valueString) {
            try {
              setConfig(JSON.parse(ext.valueString) as UserSignatureConfig);
            } catch {
              setConfig(DEFAULT_CONFIG);
            }
          }
        }
      } catch {
        // non-critical — user just won't have a saved signature
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchConfig();
    return () => { cancelled = true; };
  }, [medplum, practitioner?.id, practLoading]);

  const saveConfig = useCallback(
    async (newConfig: UserSignatureConfig): Promise<void> => {
      if (!practitioner?.id) return;

      const resource: Basic = {
        resourceType: 'Basic',
        ...(resourceId ? { id: resourceId } : {}),
        code: {
          coding: [{ system: USER_SIGNATURE_CONFIG_SYSTEM, code: USER_SIGNATURE_CONFIG_CODE }],
        },
        subject: { reference: `Practitioner/${practitioner.id}` },
        extension: [
          {
            url: USER_SIGNATURE_CONFIG_DATA_URL,
            valueString: JSON.stringify(newConfig),
          },
        ],
      };

      const saved: Basic = resourceId
        ? await medplum.updateResource(resource)
        : await medplum.createResource(resource);

      setResourceId(saved.id ?? null);
      setConfig(newConfig);
    },
    [medplum, practitioner?.id, resourceId],
  );

  return { config, loading, saveConfig };
}
